import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, Popconfirm, message } from "antd";
import { ExclamationCircleOutlined, SearchOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/GenericTable";
import editIcon from "../../../Assets/Icons/editTable.svg";
import delIcon from "../../../Assets/Icons/deleteTable.svg";
import addIcon from "../../../Assets/Icons/add-btn.svg";
import { useNavigate } from "react-router-dom";
import { getCompanyData, deleteCompany } from "../../../API/Admin/company";
import "./style.css";

const RecoveryCompany = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const errorNotification = (msg) => {
    messageApi.error(msg);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getCompanyData();

      if (Math.floor(response?.status / 100) === 2) {
        const raw = response?.data;
        const arr = Array.isArray(raw) ? raw : raw?.companies || [];
        setCompanies(arr);
      } else {
        errorNotification("Unexpected response from server.");
      }
    } catch (err) {
      errorNotification("Failed to companies data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const field = record[dataIndex];
      return field
        ? field.toString().toLowerCase().includes(value.toLowerCase())
        : false;
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const highlightText = (text) => {
    if (text === null || text === undefined) return "Not provided";
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const handleDelete = async (company_id) => {
    setLoading(true);

    try {
      const res = await deleteCompany(company_id);

      messageApi.success(res?.data?.message || "Company deleted successfully!");
      fetchData();
    } catch (error) {
      console.error("Error deleting company:", error);

      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to delete company";

      messageApi.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const companyColumns = [
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("company_name"),
    },
    {
      title: "Email Address",
      dataIndex: "email_address",
      key: "email_address",
      render: (text) => highlightText(text || "Not provided"),
      ...getColumnSearchProps("email_address"),
    },
    {
      title: "Phone Number",
      dataIndex: "primary_contact_number",
      key: "primary_contact_number",
      render: (text) => highlightText(text || "Not provided"),
      ...getColumnSearchProps("primary_contact_number"),
    },
    {
      title: "Coverage Area",
      dataIndex: "coverage_area",
      key: "coverage_area",
      render: (text) => highlightText(text || "Not provided"),
      sorter: (a, b) =>
        (a.coverage_area || "").localeCompare(
          b.coverage_area || "",
          undefined,
          { numeric: true },
        ),
      ...getColumnSearchProps("coverage_area"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span className={`status-badge ${(status || "unknown").toLowerCase()}`}>
          {status || "Unknown"}
        </span>
      ),
      filters: [
        { text: "Active", value: "Active" },
        { text: "Inactive", value: "Inactive" },
        { text: "Suspended", value: "Suspended" },
      ],
      onFilter: (value, record) => record.status === value,
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "13px" }}>
          <a onClick={() => handleEdit(record?.id)}>
            <img src={editIcon} alt="Edit" />
          </a>
          <Popconfirm
            title="Are you sure to delete this?"
            icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
            okText="Yes"
            cancelText="Cancel"
            onConfirm={() => handleDelete(record.id)}
          >
            <a>
              <img src={delIcon} alt="Delete" />
            </a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleEdit = (id) => {
    navigate(`/view-recovery-company/${id}`);
  };

  const handleClick = () => {
    navigate("/add-recovery-company");
  };

  return (
    <div className="recovery-company">
      {contextHolder}
      <div className="button">
        <Button type="default" onClick={handleClick} className="add-btn">
          <img src={addIcon} alt="add user" />
          Add Recovery Company
        </Button>
      </div>
      <div className="table">
        <GenericTable dataSource={companies} columns={companyColumns} />
      </div>
    </div>
  );
};

export default RecoveryCompany;
